import { supabase } from '../lib/supabase';

export const adminAuthService = {
  // Admin-specific login with role verification
  async adminSignIn(email, password) {
    try {
      // First authenticate with Supabase
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;

      // Verify admin role
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', data?.user?.id)
        ?.single();

      if (profileError) throw profileError;

      // Check if user has admin privileges
      if (!profile?.is_admin) {
        // Sign out non-admin user immediately
        await supabase?.auth?.signOut();
        throw new Error('Access denied. Admin privileges required.');
      }

      // Log admin login
      await this.logAdminActivity({
        userId: data?.user?.id,
        action: 'admin_login',
        details: {
          email: data?.user?.email,
          timestamp: new Date()?.toISOString()
        }
      });

      return { 
        data: {
          user: data?.user,
          profile: profile
        }, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: { message: error?.message || 'Admin login failed' } 
      };
    }
  },

  // Admin-specific logout
  async adminSignOut(userId) {
    try {
      // Log admin logout before signing out
      if (userId) {
        await this.logAdminActivity({
          userId,
          action: 'admin_logout',
          details: {
            timestamp: new Date()?.toISOString()
          }
        });
      }

      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Admin logout failed' } };
    }
  },

  // Verify admin status
  async verifyAdminAccess() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile, error } = await supabase
        ?.from('user_profiles')
        ?.select('is_admin')
        ?.eq('id', user?.id)
        ?.single();

      if (error) throw error;

      return { 
        isAdmin: profile?.is_admin || false, 
        error: null 
      };
    } catch (error) {
      return { 
        isAdmin: false, 
        error: { message: error?.message || 'Verification failed' } 
      };
    }
  },

  // Get all active admin sessions
  async getActiveSessions() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get admin activity logs for active sessions
      const { data, error } = await supabase
        ?.from('admin_activity_logs')
        ?.select('*')
        ?.eq('action', 'admin_login')
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) throw error;

      // Process and return session data
      const sessions = data?.map(log => ({
        id: log?.id,
        userId: log?.user_id,
        email: log?.details?.email,
        loginTime: log?.created_at,
        ipAddress: log?.ip_address || 'Unknown',
        location: log?.location || 'Unknown',
        deviceInfo: log?.device_info || 'Unknown'
      })) || [];

      return { data: sessions, error: null };
    } catch (error) {
      return { 
        data: [], 
        error: { message: error?.message || 'Failed to fetch sessions' } 
      };
    }
  },

  // Terminate a specific admin session
  async terminateSession(sessionId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Log session termination
      await this.logAdminActivity({
        userId: user?.id,
        action: 'session_terminated',
        details: {
          terminatedSessionId: sessionId,
          timestamp: new Date()?.toISOString()
        }
      });

      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Failed to terminate session' } };
    }
  },

  // Log admin activity
  async logAdminActivity({ userId, action, details }) {
    try {
      const { error } = await supabase
        ?.from('admin_activity_logs')
        ?.insert({
          user_id: userId,
          action: action,
          details: details,
          ip_address: 'System',
          location: 'System',
          device_info: navigator?.userAgent || 'Unknown'
        });

      if (error) console.error('Failed to log admin activity:', error);
    } catch (error) {
      console.error('Failed to log admin activity:', error);
    }
  },

  // Get admin activity logs
  async getActivityLogs(limit = 100) {
    try {
      const { data, error } = await supabase
        ?.from('admin_activity_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      return { 
        data: [], 
        error: { message: error?.message || 'Failed to fetch activity logs' } 
      };
    }
  },

  // Get security metrics
  async getSecurityMetrics() {
    try {
      const { data: logs, error } = await supabase
        ?.from('admin_activity_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(1000);

      if (error) throw error;

      // Calculate metrics
      const totalLogins = logs?.filter(log => log?.action === 'admin_login')?.length || 0;
      const failedAttempts = logs?.filter(log => log?.action === 'failed_login')?.length || 0;
      const activeSessions = logs?.filter(log => {
        const loginTime = new Date(log?.created_at);
        const now = new Date();
        const hoursSinceLogin = (now - loginTime) / (1000 * 60 * 60);
        return log?.action === 'admin_login' && hoursSinceLogin < 24;
      })?.length || 0;

      return {
        data: {
          totalLogins,
          failedAttempts,
          activeSessions,
          successRate: totalLogins > 0 ? ((totalLogins - failedAttempts) / totalLogins * 100)?.toFixed(1) : 100
        },
        error: null
      };
    } catch (error) {
      return {
        data: {
          totalLogins: 0,
          failedAttempts: 0,
          activeSessions: 0,
          successRate: 100
        },
        error: { message: error?.message || 'Failed to fetch security metrics' }
      };
    }
  }
};

export default adminAuthService;